import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, CellarTobacco, CellarPipe, CellarLighter, WishTobacco, RetailerSource } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { toast } from '../lib/toast';
import BottomNav from '../components/BottomNav';
import { Plus, DoorOpen, Edit, Trash2, Search, BarChart3, Image as ImageIcon, Droplet, NotebookPen, Star, ChevronDown } from 'lucide-react';
import { searchBlends, type BlendMatch } from '../lib/blendSearch';
import { useRetailers } from '../lib/useRetailers';
import RetailerLinks from '../components/RetailerLinks';
import WishListCard from '../components/WishListCard';
import Button from '../components/ui/Button';
import { formatDateForInput } from '../lib/dateUtils';

type TabType = 'tobacco' | 'pipes' | 'lighters';
// Extend with wishlist tab
type TabTypeExt = TabType | 'wishlist';
type SortOption = 'date-newest' | 'date-oldest' | 'brand-az' | 'blend-az';
type StatusFilter = 'all' | 'sealed' | 'opened' | 'finished';
type StorageFilter = 'all' | 'tin' | 'jar' | 'pouch' | 'bulk';

export default function MyCellar() {
  const navigate = useNavigate();
  const { currentUser } = useEmberStore();
  const [activeTab, setActiveTab] = useState<TabTypeExt>('tobacco');
  const [tobacco, setTobacco] = useState<CellarTobacco[]>([]);
  const [pipes, setPipes] = useState<CellarPipe[]>([]);
  const [lighters, setLighters] = useState<CellarLighter[]>([]);
  const [wishList, setWishList] = useState<WishTobacco[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filters & Sort
  const [sortBy, setSortBy] = useState<SortOption>('date-newest');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [storageFilter, setStorageFilter] = useState<StorageFilter>('all');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showStorageDropdown, setShowStorageDropdown] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const storageDropdownRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showStats, setShowStats] = useState(false);
  // Reminder modal state
  const [reminderTargetId, setReminderTargetId] = useState<string | null>(null);
  const [reminderQty, setReminderQty] = useState<number>(4);
  const [reminderUnit, setReminderUnit] = useState<'weeks' | 'months'>('weeks');
  const [reminderNotes, setReminderNotes] = useState('');
  // Wish add modal
  const [showWishModal, setShowWishModal] = useState(false);
  const [editingWish, setEditingWish] = useState<WishTobacco | null>(null);
  // Wishlist grid: track which item is expanded for details
  const [expandedWishId, setExpandedWishId] = useState<string | null>(null);
  // Batch detail modal
  const [selectedBatchGroup, setSelectedBatchGroup] = useState<CellarTobacco[] | null>(null);
  // Single item quick actions modal (for tobacco grid view)
  const [selectedSingleItem, setSelectedSingleItem] = useState<CellarTobacco | null>(null);
  // Pipe quick actions modal (for pipe grid view)
  const [selectedPipe, setSelectedPipe] = useState<CellarPipe | null>(null);
  const [pipePhotoViewerSrc, setPipePhotoViewerSrc] = useState<string | null>(null);
  const [pipePhotoViewerAlt, setPipePhotoViewerAlt] = useState<string>('');
  // Lighter quick actions modal (for lighter grid view)
  const [selectedLighter, setSelectedLighter] = useState<CellarLighter | null>(null);
  // Clean pipe modal state
  const [cleaningPipeId, setCleaningPipeId] = useState<string | null>(null);
  const [cleanedDate, setCleanedDate] = useState<string>(formatDateForInput());
  // Sessions indicator map: cellar_tobacco_id -> count
  const [sessionStats, setSessionStats] = useState<Record<string, { count: number; average: number | null }>>({});
  // Cached flavour tags for cellar entries
  const [tobaccoFlavorMap, setTobaccoFlavorMap] = useState<Record<string, string[]>>({});
  
  // Phase 2: Retailer data
  const { retailers, loading: retailersLoading } = useRetailers();

  // View mode toggle persisted across navigations
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('cellar_view_mode') : null;
    return (saved === 'grid' || saved === 'list') ? (saved as any) : 'list';
  });
  useEffect(() => {
    localStorage.setItem('cellar_view_mode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    loadCellarItems();
  }, [currentUser]);

  useEffect(() => {
    loadSessionStats();
  }, [currentUser]);

  useEffect(() => {
    if (!pipePhotoViewerSrc) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setPipePhotoViewerSrc(null);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pipePhotoViewerSrc]);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setShowSortDropdown(false);
      }
    }

    if (showSortDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSortDropdown]);

  // Helper function to calculate age in days between two dates
  function calculateAgeInDays(startDate: string, endDate: string): number | null {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  // Helper functions to get display labels
  function getSortLabel(value: SortOption): string {
    const labels: Record<SortOption, string> = {
      'date-newest': 'Newest',
      'date-oldest': 'Oldest',
      'brand-az': 'Blender A-Z',
      'blend-az': 'Blend A-Z',
    };
    return labels[value];
  }

  function getStatusLabel(value: StatusFilter): string {
    const labels: Record<StatusFilter, string> = {
      'all': 'All',
      'sealed': '🟢 Sealed',
      'opened': '🟡 Opened',
      'finished': '⚪ Finished',
    };
    return labels[value];
  }

  function getStorageLabel(value: StorageFilter): string {
    const labels: Record<StorageFilter, string> = {
      'all': 'All',
      'tin': '🥫 Tin',
      'jar': '🫙 Jar',
      'pouch': '📦 Pouch',
      'bulk': '📦 Bulk',
    };
    return labels[value];
  }

  async function loadCellarItems() {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      setTobaccoFlavorMap({});
      // Load tobacco
      const { data: tobaccoData } = await supabase
        .from('cellar_tobacco')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });
      
      if (tobaccoData) {
        setTobacco(tobaccoData as CellarTobacco[]);
        void loadTobaccoFlavorProfiles(tobaccoData as CellarTobacco[]);
      } else {
        setTobacco([]);
      }

      // Load pipes
      const { data: pipesData } = await supabase
        .from('cellar_pipes')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });
      
      if (pipesData) setPipes(pipesData);

      // Load lighters
      const { data: lightersData } = await supabase
        .from('cellar_lighters')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });
      
      if (lightersData) setLighters(lightersData);

      // Load wishlist
      const { data: wishData } = await supabase
        .from('wish_tobacco')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });
      if (wishData) setWishList(wishData as any);
    } catch (error) {
      console.error('Error loading cellar:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadTobaccoFlavorProfiles(items: CellarTobacco[]) {
    if (!items || items.length === 0) {
      setTobaccoFlavorMap({});
      return;
    }

    const flavorMap: Record<string, string[]> = {};

    for (const item of items) {
      const tags: string[] =
        (Array.isArray((item as any).flavor_tags) && (item as any).flavor_tags.length > 0
          ? (item as any).flavor_tags
          : []);

      if (tags.length > 0) {
        flavorMap[item.id] = tags.filter((tag) => typeof tag === 'string' && tag.trim());
        continue;
      }

      const referenceId = item.blend_ref_id || item.tobacco_id;
      if (!referenceId) continue;

      const { data, error } = await supabase
        .from('tobaccos')
        .select('id, flavor_tags')
        .eq('id', referenceId)
        .maybeSingle();

      if (error || !data) {
        console.warn('Failed to load flavour tags for cellar item:', referenceId, error);
        continue;
      }

      const remoteTags = Array.isArray(data.flavor_tags)
        ? data.flavor_tags.filter((tag) => typeof tag === 'string' && tag.trim())
        : [];

      if (remoteTags.length > 0) {
        flavorMap[item.id] = remoteTags;
      }
    }

    setTobaccoFlavorMap(flavorMap);
  }

  async function loadSessionStats() {
    if (!currentUser) return;
    const { data } = await supabase
      .from('sessions')
      .select('cellar_tobacco_id, rating_10, rating_small');
    if (data) {
      const aggregations: Record<string, { count: number; ratingSum: number; ratingCount: number }> = {};
      for (const row of data as any[]) {
        const id = row.cellar_tobacco_id as string | null;
        if (!id) continue;

        const rating10Raw = row.rating_10 as number | null | undefined;
        const ratingSmallRaw = row.rating_small as number | null | undefined;
        const ratingValue = rating10Raw != null
          ? Number(rating10Raw)
          : (ratingSmallRaw != null ? Number(ratingSmallRaw) * 2 : null);

        const entry = aggregations[id] || { count: 0, ratingSum: 0, ratingCount: 0 };
        entry.count += 1;
        if (typeof ratingValue === 'number' && !Number.isNaN(ratingValue)) {
          entry.ratingSum += ratingValue;
          entry.ratingCount += 1;
        }
        aggregations[id] = entry;
      }

      const final: Record<string, { count: number; average: number | null }> = {};
      for (const [id, value] of Object.entries(aggregations)) {
        final[id] = {
          count: value.count,
          average: value.ratingCount > 0 ? value.ratingSum / value.ratingCount : null,
        };
      }

      setSessionStats(final);
    }
  }

  async function cycleStatus(id: string, currentStatus: 'sealed' | 'opened' | 'finished') {
    // Cycle: sealed → opened → finished → sealed
    const nextStatus = currentStatus === 'sealed' ? 'opened' : currentStatus === 'opened' ? 'finished' : 'sealed';
    
    // Set opened_date when changing to 'opened'
    const updateData: { status: string; opened_date?: string } = { status: nextStatus };
    if (nextStatus === 'opened') {
      // Set opened_date to today when opening
      updateData.opened_date = new Date().toISOString().split('T')[0];
    }
    
    const { error } = await supabase
      .from('cellar_tobacco')
      .update(updateData)
      .eq('id', id);

    if (!error) {
      // Update main tobacco list
      setTobacco(tobacco.map(t => 
        t.id === id ? { ...t, status: nextStatus, ...(updateData.opened_date && { opened_date: updateData.opened_date }) } : t
      ));
      
      // Update batch modal if it's open and contains this item
      if (selectedBatchGroup) {
        setSelectedBatchGroup(selectedBatchGroup.map(item =>
          item.id === id ? { ...item, status: nextStatus, ...(updateData.opened_date && { opened_date: updateData.opened_date }) } : item
        ));
      }
    } else {
      toast.error('Failed to update status');
    }
  }

  async function showConfirmModal(title: string, message: string, confirmLabel = 'Delete') {
    return await new Promise<boolean>((resolve) => {
      const modal = document.createElement('div');
      modal.style.cssText = `position:fixed;inset:0;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:1000;padding:1rem;`;
      const content = document.createElement('div');
      content.style.cssText = `background:#1a1a1a;border:1px solid #e4574d;border-radius:12px;padding:2rem;max-width:400px;width:100%;text-align:center;color:#ffffff;`;
      content.innerHTML = `
        <h3 style="font-size:1.25rem;margin-bottom:1rem;color:#e4574d;">${title}</h3>
        <p style="color:#cfcfcf;margin-bottom:1.5rem;">${message}</p>
        <div style="display:flex;gap:1rem;justify-content:center;">
          <button id="cancel" style="padding:0.75rem 1.5rem;background:transparent;color:#999;border:1px solid #666;border-radius:6px;cursor:pointer;font-weight:600;">Cancel</button>
          <button id="confirm" style="padding:0.75rem 1.5rem;background:#e4574d;color:#fff;border:none;border-radius:6px;cursor:pointer;font-weight:600;">${confirmLabel}</button>
        </div>
      `;
      modal.appendChild(content);
      document.body.appendChild(modal);
      content.querySelector('#cancel')?.addEventListener('click', () => { modal.remove(); resolve(false); });
      content.querySelector('#confirm')?.addEventListener('click', () => { modal.remove(); resolve(true); });
    });
  }

  async function deleteTobacco(id: string, blendName: string) {
    const ok = await showConfirmModal('Delete Tobacco', `Delete "${blendName}" from your cellar? This action cannot be undone.`);
    if (!ok) return;

    const { error } = await supabase
      .from('cellar_tobacco')
      .delete()
      .eq('id', id);

    if (!error) {
      setTobacco(tobacco.filter(t => t.id !== id));
    } else {
      toast.error('Failed to delete item');
    }
  }

  async function updateStorageType(id: string, newType: 'tin' | 'jar' | 'pouch' | 'bulk' | '') {
    const newValue = newType || null;
    const { error } = await supabase
      .from('cellar_tobacco')
      .update({ storage_type: newValue })
      .eq('id', id);

    if (!error) {
      // Update main tobacco list
      setTobacco(tobacco.map(t => t.id === id ? { ...t, storage_type: newValue as any } : t));
      
      // Update batch modal if it's open and contains this item
      if (selectedBatchGroup) {
        setSelectedBatchGroup(selectedBatchGroup.map(item =>
          item.id === id ? { ...item, storage_type: newValue as any } : item
        ));
      }

      // Update single-item modal if it's open for this item
      if (selectedSingleItem?.id === id) {
        setSelectedSingleItem({ ...selectedSingleItem, storage_type: newValue as any });
      }
    } else {
      toast.error('Failed to update storage type');
    }
  }

  function computeReminderDate(fromIso: string, qty: number, unit: 'weeks' | 'months') {
    const base = new Date(fromIso);
    if (unit === 'weeks') {
      base.setDate(base.getDate() + qty * 7);
    } else {
      const d = base.getDate();
      base.setMonth(base.getMonth() + qty);
      // keep same day where possible
      if (base.getDate() !== d) base.setDate(0);
    }
    return base.toISOString();
  }

  async function saveReminder() {
    if (!reminderTargetId) return;
    const nowIso = new Date().toISOString();
    const maxQty = reminderUnit === 'weeks' ? 6 : 24;
    const safeQty = Math.max(1, Math.min(reminderQty || 1, maxQty));
    const reminderAt = computeReminderDate(nowIso, safeQty, reminderUnit);
    const { error } = await supabase
      .from('cellar_tobacco')
      .update({ reminder_at: reminderAt, reminder_notes: reminderNotes || null })
      .eq('id', reminderTargetId);
    if (!error) {
      setTobacco(tobacco.map(t => t.id === reminderTargetId ? { ...t, reminder_at: reminderAt, reminder_notes: reminderNotes || undefined } : t));
      setReminderTargetId(null);
      setReminderQty(4);
      setReminderUnit('weeks');
      setReminderNotes('');
    } else {
      toast.error('Failed to save reminder');
    }
  }

  async function clearReminder(id: string) {
    const { error } = await supabase
      .from('cellar_tobacco')
      .update({ reminder_at: null, reminder_notes: null })
      .eq('id', id);

    if (error) {
      toast.error('Failed to clear reminder');
      return;
    }

    const updater = (item: CellarTobacco) => item.id === id ? { ...item, reminder_at: undefined, reminder_notes: undefined } : item;
    setTobacco(tobacco.map(updater));

    if (selectedBatchGroup) {
      setSelectedBatchGroup(selectedBatchGroup.map(updater));
    }

    if (selectedSingleItem?.id === id) {
      setSelectedSingleItem({ ...selectedSingleItem, reminder_at: undefined, reminder_notes: undefined });
    }

    toast.success('Reminder removed');
  }

  async function deletePipe(id: string, name: string) {
    const ok = await showConfirmModal('Delete Pipe', `Delete "${name}" from your cellar? This action cannot be undone.`);
    if (!ok) return;

    const { error } = await supabase
      .from('cellar_pipes')
      .delete()
      .eq('id', id);

    if (!error) {
      setPipes(pipes.filter(p => p.id !== id));
    } else {
      toast.error('Failed to delete item');
    }
  }

  async function deleteLighter(id: string, brand: string) {
    const ok = await showConfirmModal('Delete Lighter/Matches', `Delete "${brand}" from your cellar? This action cannot be undone.`);
    if (!ok) return;

    const { error } = await supabase
      .from('cellar_lighters')
      .delete()
      .eq('id', id);

    if (!error) {
      setLighters(lighters.filter(l => l.id !== id));
    } else {
      toast.error('Failed to delete item');
    }
  }

  async function handleCleanPipe() {
    if (!cleaningPipeId || !cleanedDate) return;

    const { error } = await supabase
      .from('cellar_pipes')
      .update({ last_cleaned_date: cleanedDate })
      .eq('id', cleaningPipeId);

    if (!error) {
      // Update local state
      setPipes(pipes.map(p => 
        p.id === cleaningPipeId ? { ...p, last_cleaned_date: cleanedDate } : p
      ));
      toast.success('Pipe cleaned date saved!');
      setCleaningPipeId(null);
    } else {
      toast.error('Failed to save cleaned date');
    }
  }

  // Filter and sort tobacco
  function getFilteredAndSortedTobacco() {
    let filtered = [...tobacco];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    // Apply storage filter
    if (storageFilter !== 'all') {
      filtered = filtered.filter(t => t.storage_type === storageFilter);
    }

    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.brand.toLowerCase().includes(search) || 
        t.blend_name.toLowerCase().includes(search)
      );
    }

    // Apply sort
    switch (sortBy) {
      case 'date-newest':
        filtered.sort((a, b) => new Date(b.purchase_date).getTime() - new Date(a.purchase_date).getTime());
        break;
      case 'date-oldest':
        filtered.sort((a, b) => new Date(a.purchase_date).getTime() - new Date(b.purchase_date).getTime());
        break;
      case 'brand-az':
        filtered.sort((a, b) => a.brand.localeCompare(b.brand));
        break;
      case 'blend-az':
        filtered.sort((a, b) => a.blend_name.localeCompare(b.blend_name));
        break;
    }

    return filtered;
  }

  // Calculate statistics
  interface CellarStats {
    totalItems: number;
    totalGrams: number;
    totalOunces: number;
    combinedGrams: number;
    combinedOunces: number;
    sealed: number;
    opened: number;
    finished: number;
    byStorage: Record<string, number>;
    topBrands: Array<[string, number]>;
    topFlavors: Array<{ label: string; count: number }>;
  }

  function getStats(): CellarStats {
    let totalGrams = 0;
    let totalOunces = 0;
    for (const item of tobacco) {
      if (item.unit === 'grams') {
        totalGrams += item.amount;
      } else if (item.unit === 'ounces') {
        totalOunces += item.amount;
      }
    }
    const totalItems = tobacco.length;
    const sealed = tobacco.filter(t => t.status === 'sealed').length;
    const opened = tobacco.filter(t => t.status === 'opened').length;
    const finished = tobacco.filter(t => t.status === 'finished').length;
    
    const byStorage = tobacco.reduce((acc, t) => {
      const type = t.storage_type || 'none';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const brandCounts = tobacco.reduce((acc, t) => {
      acc[t.brand] = (acc[t.brand] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topBrands = Object.entries(brandCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3);

    const flavorCounts: Record<string, { count: number; label: string }> = {};
    for (const item of tobacco) {
      const tags = tobaccoFlavorMap[item.id];
      if (!tags || tags.length === 0) continue;
      for (const rawTag of tags) {
        const trimmed = rawTag.trim();
        if (!trimmed) continue;
        const normalized = trimmed.toLowerCase();
        const label = trimmed
          .split(/[\s-_]+/)
          .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : ''))
          .join(' ');
        if (!flavorCounts[normalized]) {
          flavorCounts[normalized] = { count: 0, label };
        }
        flavorCounts[normalized].count += 1;
      }
    }

    const topFlavors = Object.values(flavorCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    const gramsPerOunce = 28.3495;
    const combinedGrams = totalGrams + totalOunces * gramsPerOunce;
    const combinedOunces = totalOunces + totalGrams / gramsPerOunce;

    return {
      totalItems,
      totalGrams,
      totalOunces,
      combinedGrams,
      combinedOunces,
      sealed,
      opened,
      finished,
      byStorage,
      topBrands,
      topFlavors
    };
  }

  function getActiveItems() {
    switch (activeTab) {
      case 'tobacco':
        return tobacco;
      case 'pipes':
        return pipes;
      case 'lighters':
        return lighters;
      default:
        return [];
    }
  }

  const activeItems = getActiveItems();
  const filteredTobacco = activeTab === 'tobacco' ? getFilteredAndSortedTobacco() : [];
  // Group tobacco by batch_id (non-null) else by id to treat singletons like single groups
  const tobaccoGroups = activeTab === 'tobacco' ? filteredTobacco.reduce((acc: Record<string, CellarTobacco[]>, item) => {
    const key = item.batch_id || item.id;
    (acc[key] ||= []).push(item);
    return acc;
  }, {} as Record<string, CellarTobacco[]>) : {} as Record<string, CellarTobacco[]>;
  const tobaccoGroupKeys = activeTab === 'tobacco' ? Object.keys(tobaccoGroups) : [];
  const stats = activeTab === 'tobacco' ? getStats() : null;

  const formatQuantity = (value: number) => {
    if (!Number.isFinite(value)) {
      return '0';
    }
    const hasFraction = Math.abs(value % 1) > Number.EPSILON;
    return value.toLocaleString(undefined, hasFraction
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { maximumFractionDigits: 0 });
  };

  const handleExportCellar = () => {
    if (!tobacco.length) {
      toast.info('No cellar items to export yet');
      return;
    }

    const groupedByBatch = new Map<string, {
      blend: string;
      brand: string;
      amount: number;
      unit: string;
      storage: string;
      status: string;
      purchaseDate: string;
      multiples: number;
    }>();

    for (const item of tobacco) {
      const key = item.batch_id || item.id;
      const existing = groupedByBatch.get(key);
      if (existing) {
        existing.multiples += 1;
        // keep earliest purchase date
        if (item.purchase_date < existing.purchaseDate) {
          existing.purchaseDate = item.purchase_date;
        }
      } else {
        groupedByBatch.set(key, {
          blend: item.blend_name,
          brand: item.brand,
          amount: item.amount,
          unit: item.unit,
          storage: item.storage_type || '',
          status: item.status,
          purchaseDate: item.purchase_date,
          multiples: 1,
        });
      }
    }

    const rows = Array.from(groupedByBatch.values());

    const headers = ['Blend', 'Blender', 'Amount', 'Unit', 'Multiples', 'Storage', 'Status', 'Purchase Date'];

    const escapeCsv = (value: string | number | null | undefined) => {
      const str = value == null ? '' : String(value);
      return `"${str.replace(/"/g, '""')}"`;
    };

    const csvLines = [
      headers.map(escapeCsv).join(','),
      ...rows.map((row) =>
        [
          row.blend,
          row.brand,
          row.amount,
          row.unit,
          row.multiples,
          row.storage,
          row.status,
          new Date(row.purchaseDate).toISOString().split('T')[0],
        ].map(escapeCsv).join(',')
      ),
    ];

    const csvContent = csvLines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ember-cellar-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 0);

    toast.success('Cellar exported as CSV');
  };

  // Wish form component with fuzzy-match blend search
  function WishForm({ onAdded }: { onAdded: (w: WishTobacco) => void }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [wBrand, setWBrand] = useState('');
    const [wBlend, setWBlend] = useState('');
    const [wImage, setWImage] = useState<File | null>(null);
    const [wPreview, setWPreview] = useState<string | null>(null);
    const [wNotes, setWNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [blendMatches, setBlendMatches] = useState<BlendMatch[]>([]);
    const [showMatches, setShowMatches] = useState(false);
    const [selectedBlend, setSelectedBlend] = useState<BlendMatch | null>(null);
    const [searching, setSearching] = useState(false);
    const searchTimeoutRef = useRef<number | null>(null);
    // When a user selects a suggestion, suppress the next debounced search
    // so the dropdown doesn't immediately reopen with the selected query
    const suppressNextSearchRef = useRef(false);

    // Debounced fuzzy search (triggered straight from onChange)
    async function queueBlendSearch(nextQuery: string) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      const trimmed = nextQuery.trim();
      if (trimmed.length < 2) {
        setBlendMatches([]);
        setShowMatches(false);
        setSearching(false);
        return;
      }

      setSearching(true);
      searchTimeoutRef.current = window.setTimeout(async () => {
        const { data } = await searchBlends(trimmed);
        if (data) {
          setBlendMatches(data);
          setShowMatches(data.length > 0);
        } else {
          setBlendMatches([]);
          setShowMatches(false);
        }
        setSearching(false);
      }, 300);
    }

    function selectMatch(match: BlendMatch) {
      setSelectedBlend(match);
      setWBrand(match.brand);
      setWBlend(match.blend_name);
      setSearchQuery(`${match.brand} ${match.blend_name}`);
      suppressNextSearchRef.current = true;
      setShowMatches(false);
    }

    function addAsCustom() {
      setSelectedBlend(null);
      setShowMatches(false);
    }

    async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
      const f = e.target.files?.[0] || null;
      setWImage(f);
      if (f) {
        const r = new FileReader();
        r.onload = ev => setWPreview(ev.target?.result as string);
        r.readAsDataURL(f);
      } else {
        setWPreview(null);
      }
    }

    async function uploadWishImage(): Promise<string | null> {
      if (!wImage || !currentUser) return null;
      const filePath = `${currentUser.id}/wish-${Date.now()}-${wImage.name}`;
      const { data, error } = await supabase.storage.from('cellar-images').upload(filePath, wImage, { upsert: false });
      if (error || !data) return null;
      const { data: pub } = supabase.storage.from('cellar-images').getPublicUrl(data.path);
      return pub?.publicUrl ?? null;
    }

    async function addWish() {
      if (!currentUser) return;
      if (!wBrand.trim() || !wBlend.trim()) return;
      setSaving(true);
      try {
        const image_url = await uploadWishImage();
        const wishData: any = {
          user_id: currentUser.id,
          brand: wBrand.trim(),
          blend_name: wBlend.trim(),
          image_url,
          notes: wNotes.trim() || null,
        };

        // If user selected a blend match, include the reference
        if (selectedBlend) {
          wishData.blend_ref_id = selectedBlend.id;
          wishData.matched_confidence = selectedBlend.similarity;
        }

        const { data, error } = await supabase
          .from('wish_tobacco')
          .insert(wishData)
          .select('*')
          .single();
        if (error) throw error;
        onAdded(data as any);
        
        // Reset form
        setSearchQuery('');
        setWBrand('');
        setWBlend('');
        setWImage(null);
        setWPreview(null);
        setWNotes('');
        setSelectedBlend(null);
        setBlendMatches([]);
        setShowMatches(false);
      } finally {
        setSaving(false);
      }
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {/* Search Input with Autocomplete */}
        <div style={{ position: 'relative', zIndex: 1000 }}>
          <div style={{ position: 'relative' }}>
            <Search size={18} color="#666" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              placeholder="Search blend (e.g., Escudo, Nightcap)..."
              value={searchQuery}
              onChange={(e) => {
                const value = e.target.value;
                setSearchQuery(value);
                setShowMatches(true);
                if (!value) {
                  setSelectedBlend(null);
                  setWBrand('');
                  setWBlend('');
                  setBlendMatches([]);
                  setShowMatches(false);
                  setSearching(false);
                  return;
                }
                if (suppressNextSearchRef.current) {
                  suppressNextSearchRef.current = false;
                  return;
                }
                queueBlendSearch(value);
              }}
              style={{
                width: '100%',
                paddingLeft: '2.5rem',
                backgroundColor: selectedBlend ? 'rgba(196, 255, 0, 0.1)' : undefined,
                border: selectedBlend ? '1px solid var(--color-accent)' : undefined
              }}
            />
            {searching && (
              <div style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)' }}>
                <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
              </div>
            )}
          </div>

          {/* Autocomplete Dropdown - Fixed positioning */}
          {showMatches && blendMatches.length > 0 && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              backgroundColor: '#1a1a1a',
              border: '2px solid var(--color-accent)',
              borderRadius: '8px',
              maxHeight: '240px',
              overflowY: 'auto',
              zIndex: 10000,
              boxShadow: '0 12px 32px rgba(0,0,0,0.9)'
            }}>
              {blendMatches.map((match) => (
                <button
                  key={match.id}
                  onClick={() => selectMatch(match)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    textAlign: 'left',
                    border: 'none',
                    borderBottom: '1px solid var(--color-border)',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-card)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '0.5rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#ffffff' }}>
                        {match.brand} - {match.blend_name}
                      </div>
                      {match.blend_type && (
                        <div style={{ fontSize: '0.75rem', color: '#cccccc', marginTop: '0.25rem' }}>
                          {match.blend_type}
                        </div>
                      )}
                    </div>
                    {match.avg_rating && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-accent)', whiteSpace: 'nowrap' }}>
                        ⭐ {match.avg_rating.toFixed(1)}
                      </div>
                    )}
                  </div>
                </button>
              ))}
              <button
                onClick={addAsCustom}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  textAlign: 'center',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: '#ffffff',
                  fontStyle: 'italic'
                }}
              >
                Add "{searchQuery}" as custom blend
              </button>
            </div>
          )}
        </div>

        {/* Selected Blend Info */}
        {selectedBlend && (
          <div style={{
            padding: '0.5rem',
            backgroundColor: 'rgba(196, 255, 0, 0.1)',
            border: '1px solid var(--color-accent)',
            borderRadius: '6px',
            fontSize: '0.75rem'
          }}>
            <div style={{ color: 'var(--color-accent)', fontWeight: 600 }}>
              ✓ Matched: {selectedBlend.brand} - {selectedBlend.blend_name}
            </div>
            {selectedBlend.avg_rating && (
              <div style={{ color: '#999', marginTop: '0.25rem' }}>
                ⭐ {selectedBlend.avg_rating.toFixed(1)} ({selectedBlend.review_count} reviews)
                {selectedBlend.blend_type && ` • ${selectedBlend.blend_type}`}
              </div>
            )}
          </div>
        )}

        {/* Manual Entry Fields (fallback) */}
        <input placeholder="Blender" value={wBrand} onChange={(e) => setWBrand(e.target.value)} style={{ width: '100%' }} />
        <input placeholder="Blend" value={wBlend} onChange={(e) => setWBlend(e.target.value)} style={{ width: '100%' }} />

        {wPreview && (
          <div className="card-photo">
            <img
              src={wPreview}
              alt="Preview"
              style={{ width: '100%', maxHeight: '140px', objectFit: 'contain', borderRadius: '8px', backgroundColor: '#111' }}
            />
          </div>
        )}
        {!wPreview && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <label htmlFor="wish-photo-gallery" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '1rem', border: '2px dashed #333', borderRadius: '8px', cursor: 'pointer', backgroundColor: 'var(--color-card)', transition: 'border-color 0.2s' }}>
              <ImageIcon size={24} color="var(--color-accent)" />
              <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Choose from Gallery</span>
            </label>
            <label htmlFor="wish-photo-camera" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '1rem', border: '2px dashed #333', borderRadius: '8px', cursor: 'pointer', backgroundColor: 'var(--color-card)', transition: 'border-color 0.2s' }}>
              <ImageIcon size={24} color="var(--color-accent)" />
              <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Take Photo</span>
            </label>
            <input id="wish-photo-gallery" type="file" accept="image/*" onChange={(e) => onFile(e as any)} style={{ display: 'none' }} />
            <input id="wish-photo-camera" type="file" accept="image/*" capture="environment" onChange={(e) => onFile(e as any)} style={{ display: 'none' }} />
          </div>
        )}
        <textarea placeholder="Notes (optional)" value={wNotes} onChange={(e) => setWNotes(e.target.value)} rows={2} />
        <div style={{ marginTop: '0.75rem' }}>
          <Button
            type="button"
            onClick={addWish}
            disabled={saving || !wBrand.trim() || !wBlend.trim()}
            loading={saving}
            fullWidth
          >
            {saving ? 'Adding...' : 'Add to Wish List'}
          </Button>
        </div>
      </div>
    );
  }

  async function deleteWish(id: string) {
    const { error } = await supabase.from('wish_tobacco').delete().eq('id', id);
    if (!error) setWishList(wishList.filter(w => w.id !== id));
  }

  function handlePurchaseWish(w: WishTobacco) {
    // Navigate to AddCellarItem with prefill via query params
    const params = new URLSearchParams({ brand: w.brand, blend: w.blend_name, image: w.image_url || '', notes: w.notes || '' });
    window.location.href = `/cellar/add?prefill=1&${params.toString()}`;
  }

  // Edit wish modal logic
  async function saveWishEdits() {
    if (!editingWish) return;
    const { id, brand, blend_name, notes, image_url } = editingWish;
    const { error } = await supabase
      .from('wish_tobacco')
      .update({ brand, blend_name, notes, image_url })
      .eq('id', id);
    if (!error) {
      setWishList(wishList.map((w: WishTobacco) => w.id === id ? editingWish : w));
      setEditingWish(null);
    }
  }
  return (
    <div style={{ minHeight: '100vh', paddingBottom: '80px', position: 'relative' }}>
      {/* Header */}
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.75rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <DoorOpen size={24} color="var(--color-accent)" />
          <h1 style={{ fontSize: '1.5rem', fontFamily: '"Oswald", sans-serif', letterSpacing: '-0.02em' }}>
            My Cellar
          </h1>
        </div>
        <button
          onClick={() => navigate('/cellar/add')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
          backgroundColor: 'var(--color-secondary)',
          color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '0.875rem'
          }}
        >
          <Plus size={16} />
          Add Item
        </button>
      </header>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '2px solid var(--color-border)',
        backgroundColor: 'var(--color-card)',
      }}>
        <button
          onClick={() => setActiveTab('tobacco')}
          style={{
            flex: 1,
            padding: '1rem',
            border: 'none',
            background: 'none',
            color: activeTab === 'tobacco' ? 'var(--color-accent)' : 'var(--color-text)',
            borderBottom: activeTab === 'tobacco' ? '3px solid var(--color-accent)' : '3px solid transparent',
            cursor: 'pointer',
            fontWeight: activeTab === 'tobacco' ? '600' : '400',
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            transition: 'all 0.2s',
          }}
        >
          Tobacco ({tobacco.length})
        </button>

        <button
          onClick={() => setActiveTab('pipes')}
          style={{
            flex: 1,
            padding: '1rem',
            border: 'none',
            background: 'none',
            color: activeTab === 'pipes' ? 'var(--color-accent)' : 'var(--color-text)',
            borderBottom: activeTab === 'pipes' ? '3px solid var(--color-accent)' : '3px solid transparent',
            cursor: 'pointer',
            fontWeight: activeTab === 'pipes' ? '600' : '400',
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            transition: 'all 0.2s',
          }}
        >
          Pipes ({pipes.length})
        </button>

        <button
          onClick={() => setActiveTab('lighters')}
          style={{
            flex: 1,
            padding: '1rem',
            border: 'none',
            background: 'none',
            color: activeTab === 'lighters' ? 'var(--color-accent)' : 'var(--color-text)',
            borderBottom: activeTab === 'lighters' ? '3px solid var(--color-accent)' : '3px solid transparent',
            cursor: 'pointer',
            fontWeight: activeTab === 'lighters' ? '600' : '400',
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            transition: 'all 0.2s',
          }}
        >
          Lighters ({lighters.length})
        </button>

        <button
          onClick={() => setActiveTab('wishlist')}
          style={{
            flex: 1,
            padding: '1rem',
            border: 'none',
            background: 'none',
            color: activeTab === 'wishlist' ? 'var(--color-accent)' : 'var(--color-text)',
            borderBottom: activeTab === 'wishlist' ? '3px solid var(--color-accent)' : '3px solid transparent',
            cursor: 'pointer',
            fontWeight: activeTab === 'wishlist' ? '600' : '400',
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            transition: 'all 0.2s',
          }}
        >
          Wish List ({wishList.length})
        </button>
      </div>

      {/* Statistics Card - Tobacco Tab Only */}
      {activeTab === 'tobacco' && tobacco.length > 0 && stats && (
        <div className="container" style={{ paddingTop: '1rem' }}>
          <div className="card" style={{ backgroundColor: '#0d0d0d', border: '1px solid var(--color-accent)' }}>
            <div 
              onClick={() => setShowStats(!showStats)}
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <BarChart3 size={20} color="var(--color-accent)" />
                <h3 style={{ fontSize: '1rem', fontWeight: '600' }}>Collection Stats</h3>
              </div>
              <span style={{ fontSize: '0.875rem', color: 'var(--color-accent)' }}>
                {`${stats.totalItems} ${stats.totalItems === 1 ? 'item' : 'items'}, ${formatQuantity(stats.totalGrams)} grams, ${formatQuantity(stats.totalOunces)} oz`}
              </span>
            </div>

            {showStats && (
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={handleExportCellar}
                  >
                    Export Cellar
                  </Button>
                </div>
                <div style={{ fontSize: '0.875rem', color: '#cfcfcf', marginBottom: '1rem' }}>
                  Combined Total: {formatQuantity(stats.combinedGrams)}g / {formatQuantity(stats.combinedOunces)}oz
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--color-success)' }}>{stats.sealed}</div>
                    <div style={{ fontSize: '0.75rem', color: '#999' }}>🟢 Sealed</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#facc15' }}>{stats.opened}</div>
                    <div style={{ fontSize: '0.75rem', color: '#999' }}>🟡 Opened</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#6b7280' }}>{stats.finished}</div>
                    <div style={{ fontSize: '0.75rem', color: '#999' }}>⚪ Finished</div>
                  </div>
                </div>

                {stats.topBrands.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--color-accent)' }}>
                      Top Brands:
                    </h4>
                    {stats.topBrands.map(([brand, count]) => (
                      <div key={brand} style={{ fontSize: '0.875rem', color: '#cfcfcf', marginBottom: '0.25rem' }}>
                        {brand} ({count})
                      </div>
                    ))}
                  </div>
                )}

            {stats.topFlavors.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--color-accent)' }}>
                  Top Flavour Profiles:
                </h4>
                {stats.topFlavors.map((flavor) => (
                  <div key={flavor.label} style={{ fontSize: '0.875rem', color: '#cfcfcf', marginBottom: '0.25rem' }}>
                    {flavor.label} ({flavor.count})
                  </div>
                ))}
              </div>
            )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters & Search - Tobacco Tab Only */}
      {activeTab === 'tobacco' && tobacco.length > 0 && (
        <div className="container" style={{ paddingTop: '1rem' }}>
          <div className="card" style={{ backgroundColor: '#0d0d0d' }}>
            {/* Search */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ position: 'relative' }}>
                <Search size={18} color="#666" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search brand or blend..."
                  style={{ 
                    width: '100%',
                    paddingLeft: '2.5rem',
                  }}
                />
              </div>
            </div>

            {/* Sort & Filter */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
              <div ref={sortDropdownRef} style={{ position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.75rem', color: '#999' }}>
                  Sort By
                </label>
                <button
                  type="button"
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.75rem',
                    backgroundColor: 'var(--color-card)',
                    border: `1px solid ${showSortDropdown ? 'var(--color-accent)' : 'var(--color-border)'}`,
                    borderRadius: '8px',
                    color: 'var(--color-text)',
                    textAlign: 'left',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    lineHeight: '1.5',
                    minHeight: 'unset',
                    height: 'auto',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-accent)';
                  }}
                  onBlur={(e) => {
                    if (!showSortDropdown) {
                      e.currentTarget.style.borderColor = 'var(--color-border)';
                    }
                  }}
                >
                  <span style={{ flex: 1, textAlign: 'left', lineHeight: '1.5' }}>{getSortLabel(sortBy)}</span>
                  <ChevronDown size={10} style={{ opacity: 0.7, flexShrink: 0, marginLeft: '0.25rem', lineHeight: 1 }} />
                </button>

                {/* Custom Dropdown Menu */}
                {showSortDropdown && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: 'var(--color-card)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                      overflowY: 'visible',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                    }}
                  >
                    {(['date-newest', 'date-oldest', 'brand-az', 'blend-az'] as SortOption[]).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          setSortBy(option);
                          setShowSortDropdown(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          fontSize: '0.75rem',
                          backgroundColor: sortBy === option ? 'rgba(196, 255, 0, 0.1)' : 'transparent',
                          border: 'none',
                          borderBottom: '1px solid var(--color-border)',
                          color: sortBy === option ? 'var(--color-accent)' : 'var(--color-text)',
                          textAlign: 'left',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                        onMouseEnter={(e) => {
                          if (sortBy !== option) {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (sortBy !== option) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {getSortLabel(option)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div ref={statusDropdownRef} style={{ position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.75rem', color: '#999' }}>
                  Status
                </label>
                <button
                  type="button"
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.75rem',
                    backgroundColor: 'var(--color-card)',
                    border: `1px solid ${showStatusDropdown ? 'var(--color-accent)' : 'var(--color-border)'}`,
                    borderRadius: '8px',
                    color: 'var(--color-text)',
                    textAlign: 'left',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    lineHeight: '1.5',
                    minHeight: 'unset',
                    height: 'auto',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-accent)';
                  }}
                  onBlur={(e) => {
                    if (!showStatusDropdown) {
                      e.currentTarget.style.borderColor = 'var(--color-border)';
                    }
                  }}
                >
                  <span style={{ flex: 1, textAlign: 'left', lineHeight: '1.5' }}>{getStatusLabel(statusFilter)}</span>
                  <ChevronDown size={10} style={{ opacity: 0.7, flexShrink: 0, marginLeft: '0.25rem', lineHeight: 1 }} />
                </button>

                {/* Custom Dropdown Menu */}
                {showStatusDropdown && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: 'var(--color-card)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                      overflowY: 'visible',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                    }}
                  >
                    {(['all', 'sealed', 'opened', 'finished'] as StatusFilter[]).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          setStatusFilter(option);
                          setShowStatusDropdown(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          fontSize: '0.75rem',
                          backgroundColor: statusFilter === option ? 'rgba(196, 255, 0, 0.1)' : 'transparent',
                          border: 'none',
                          borderBottom: '1px solid var(--color-border)',
                          color: statusFilter === option ? 'var(--color-accent)' : 'var(--color-text)',
                          textAlign: 'left',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                        onMouseEnter={(e) => {
                          if (statusFilter !== option) {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (statusFilter !== option) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {getStatusLabel(option)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div ref={storageDropdownRef} style={{ position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.75rem', color: '#999' }}>
                  Storage
                </label>
                <button
                  type="button"
                  onClick={() => setShowStorageDropdown(!showStorageDropdown)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.75rem',
                    backgroundColor: 'var(--color-card)',
                    border: `1px solid ${showStorageDropdown ? 'var(--color-accent)' : 'var(--color-border)'}`,
                    borderRadius: '8px',
                    color: 'var(--color-text)',
                    textAlign: 'left',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    lineHeight: '1.5',
                    minHeight: 'unset',
                    height: 'auto',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-accent)';
                  }}
                  onBlur={(e) => {
                    if (!showStorageDropdown) {
                      e.currentTarget.style.borderColor = 'var(--color-border)';
                    }
                  }}
                >
                  <span style={{ flex: 1, textAlign: 'left', lineHeight: '1.5' }}>{getStorageLabel(storageFilter)}</span>
                  <ChevronDown size={10} style={{ opacity: 0.7, flexShrink: 0, marginLeft: '0.25rem', lineHeight: 1 }} />
                </button>

                {/* Custom Dropdown Menu */}
                {showStorageDropdown && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: 'var(--color-card)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                      overflowY: 'visible',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                    }}
                  >
                    {(['all', 'tin', 'jar', 'pouch', 'bulk'] as StorageFilter[]).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          setStorageFilter(option);
                          setShowStorageDropdown(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          fontSize: '0.75rem',
                          backgroundColor: storageFilter === option ? 'rgba(196, 255, 0, 0.1)' : 'transparent',
                          border: 'none',
                          borderBottom: '1px solid var(--color-border)',
                          color: storageFilter === option ? 'var(--color-accent)' : 'var(--color-text)',
                          textAlign: 'left',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                        onMouseEnter={(e) => {
                          if (storageFilter !== option) {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (storageFilter !== option) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {getStorageLabel(option)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* View toggle */}
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '0.75rem' }}>
            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
              style={{ padding: '0.4rem 0.8rem', backgroundColor: viewMode === 'grid' ? 'var(--color-accent)' : 'var(--color-card)', border: '1px solid var(--color-border)', color: viewMode === 'grid' ? '#0b0b0b' : '#ccc', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              View
            </button>
          </div>
        </div>
      )}

      {/* View toggle for pipes - separate section */}
      {activeTab === 'pipes' && pipes.length > 0 && (
        <div className="container" style={{ paddingTop: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '0.75rem' }}>
            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
              style={{ padding: '0.4rem 0.8rem', backgroundColor: viewMode === 'grid' ? 'var(--color-accent)' : 'var(--color-card)', border: '1px solid var(--color-border)', color: viewMode === 'grid' ? '#0b0b0b' : '#ccc', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              View
            </button>
          </div>
        </div>
      )}

      {/* View toggle for lighters - separate section */}
      {activeTab === 'lighters' && lighters.length > 0 && (
        <div className="container" style={{ paddingTop: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '0.75rem' }}>
            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
              style={{ padding: '0.4rem 0.8rem', backgroundColor: viewMode === 'grid' ? 'var(--color-accent)' : 'var(--color-card)', border: '1px solid var(--color-border)', color: viewMode === 'grid' ? '#0b0b0b' : '#ccc', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              View
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="container" style={{ paddingTop: '1rem', paddingBottom: '5rem' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
            <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
            Loading cellar...
          </div>
        ) : ((activeTab !== 'wishlist' && activeItems.length === 0) || (activeTab === 'wishlist' && wishList.length === 0)) ? (
          // Empty State
          <div style={{ 
            textAlign: 'center', 
            padding: '3rem 1rem',
            color: '#999',
          }}>
            <DoorOpen size={64} color="#333" style={{ margin: '0 auto 1rem' }} />
            <h3 style={{ fontSize: '1.125rem', marginBottom: '0.5rem', color: 'var(--color-text)' }}>
              Your {activeTab} collection is empty
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Start building your collection by adding items to your cellar.
            </p>
          </div>
        ) : (
          // Items List
          <div style={{
            display: activeTab === 'wishlist' ? 'flex' : (viewMode === 'grid' ? 'grid' : 'flex'),
            gridTemplateColumns: activeTab !== 'wishlist' && viewMode === 'grid' ? 'repeat(3, minmax(0, 1fr))' : undefined,
            gap: '1rem',
            flexDirection: activeTab === 'wishlist' ? 'column' : (viewMode === 'list' ? 'column' : undefined)
          }}>
            {activeTab === 'tobacco' && filteredTobacco.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                No tobacco matches your filters. Try adjusting your search or filters.
              </div>
            ) : activeTab === 'wishlist' ? (
              <>
                {/* Add new wish */}
                <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>Add to Wish List</h3>
                  <WishForm onAdded={(w) => setWishList([w, ...wishList])} />
                </div>
                {/* View toggle for wishlist */}
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <button
                    onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
                    style={{ padding: '0.4rem 0.8rem', backgroundColor: viewMode === 'grid' ? 'var(--color-accent)' : 'var(--color-card)', border: '1px solid var(--color-border)', color: viewMode === 'grid' ? '#0b0b0b' : '#ccc', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
                  >
                    View
                  </button>
                </div>
                {/* Wish list items */}
                {wishList.length === 0 ? (
                  <div className="card" style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Your wish list is empty</div>
                ) : viewMode === 'grid' ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '1rem' }}>
                    {wishList.map((w) => (
                      <div key={w.id} className="card" style={{ padding: '0.75rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <button
                            onClick={() => setExpandedWishId(expandedWishId === w.id ? null : w.id)}
                            aria-expanded={expandedWishId === w.id}
                            title="View details"
                            style={{ width: '100%', aspectRatio: '1 / 1', borderRadius: '8px', overflow: 'hidden', background: '#111', border: '1px solid var(--color-border)', padding: 0, display: 'block', cursor: 'pointer' }}
                          >
                            {w.image_url ? (
                              <img src={w.image_url} alt={`${w.brand} ${w.blend_name}`} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            ) : (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>No Image</div>
                            )}
                          </button>
                          <div style={{ width: '100%' }}>
                            <div style={{ fontSize: '0.9rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.brand}</div>
                            <div style={{ fontSize: '0.8rem', color: '#d3de5e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.blend_name}</div>
                          </div>

                          {/* In grid mode we open a modal instead of inline expansion to avoid row height shifts */}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  wishList.map((w) => (
                    <WishListCard 
                      key={w.id} 
                      item={w} 
                      retailers={retailers}
                      onEdit={setEditingWish}
                      onDelete={deleteWish}
                      onPurchase={handlePurchaseWish}
                    />
                  ))
                )}
              </>
            ) : activeTab === 'tobacco' && tobaccoGroupKeys.map((groupKey) => {
              const group = tobaccoGroups[groupKey];
              const item = group[0];
              const isBatch = !!item.batch_id && group.length > 1;
              return (
              <div 
                key={groupKey} 
                className="card" 
                style={{ 
                  display: viewMode === 'grid' ? 'block' : 'flex',
                  gap: viewMode === 'grid' ? undefined : '1rem',
                  flexDirection: viewMode === 'grid' ? undefined : 'row',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                }}
                onClick={() => {
                  if (isBatch) {
                    setSelectedBatchGroup(group);
                  } else {
                    // Open single item modal in both grid and list view
                    setSelectedSingleItem(item);
                  }
                }}
                onMouseEnter={(e) => {
                  if (isBatch) {
                    e.currentTarget.style.borderColor = 'var(--color-accent)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isBatch) {
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                  }
                }}
              >
                {/* Image */}
                {viewMode === 'grid' ? (
                  <div style={{ marginBottom: '0.5rem' }}>
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.blend_name}
                        loading="lazy"
                        style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                      />
                    ) : (
                      <div style={{ width: '100%', aspectRatio: '1 / 1', backgroundColor: '#1f1f1f', borderRadius: '8px', border: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>No Image</div>
                    )}
                  </div>
                ) : (
                  item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.blend_name}
                      loading="lazy"
                      style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }}
                    />
                  ) : (
                    <div style={{ width: '80px', height: '80px', backgroundColor: '#1f1f1f', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <DoorOpen size={32} color="#666" />
                    </div>
                  )
                )}

                {/* Info */}
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: viewMode === 'grid' ? '0.95rem' : '1rem', fontWeight: '600', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.blend_name}
                  </h3>
                  <p style={{ fontSize: viewMode === 'grid' ? '0.85rem' : '0.875rem', color: '#d3de5e', marginBottom: '0.5rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    {item.brand || 'Unknown blender'}
                    {sessionStats[item.id]?.count ? (
                      <span
                        title={`${sessionStats[item.id]!.count} session${sessionStats[item.id]!.count > 1 ? 's' : ''}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.15rem', color: 'var(--color-accent)' }}
                      >
                        <NotebookPen size={14} /> {sessionStats[item.id]!.count}
                      </span>
                    ) : null}
                    {sessionStats[item.id]?.average != null ? (
                      <span
                        title={`Average rating ${sessionStats[item.id]!.average!.toFixed(1)}/10`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', color: '#ffb347' }}
                      >
                        <Star size={14} fill="#ffb347" color="#ffb347" />
                        {sessionStats[item.id]!.average!.toFixed(1)}
                      </span>
                    ) : null}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', color: '#999', fontSize: viewMode === 'grid' ? '0.8rem' : '0.875rem', marginBottom: viewMode === 'grid' ? '0.25rem' : '0.5rem' }}>
                    <span>
                      {item.amount}{item.unit === 'grams' ? 'g' : 'oz'}
                      {item.storage_type && ` • ${
                        item.storage_type === 'tin'
                          ? '🥫'
                          : item.storage_type === 'jar'
                          ? '🫙'
                          : item.storage_type === 'pouch'
                          ? '👝'
                          : '📦'
                      } ${item.storage_type}`}
                    </span>
                    {isBatch && <span style={{ fontSize: '0.75rem', backgroundColor: 'var(--color-accent)', border: '1px solid var(--color-accent)', color: '#0b0b0b', padding: '0.2rem 0.5rem', borderRadius: '999px', fontWeight: 600 }}>×{group.length}</span>}
                  </div>
                  {viewMode === 'list' && (
                    <>
                      <p style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
                        Purchased: {new Date(item.purchase_date).toLocaleDateString()}
                      </p>
                      {item.tinned_date && (
                        <>
                          <p style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
                            Tinned: {new Date(item.tinned_date).toLocaleDateString()}
                          </p>
                          {(() => {
                            const ageDays = calculateAgeInDays(item.tinned_date, item.purchase_date);
                            return ageDays !== null ? (
                              <p style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.25rem' }}>
                                Age: {ageDays} day{ageDays !== 1 ? 's' : ''}
                              </p>
                            ) : null;
                          })()}
                        </>
                      )}
                      {item.status === 'opened' && item.opened_date && (
                        <p style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
                          Opened: {new Date(item.opened_date).toLocaleDateString()}
                        </p>
                      )}
                      {item.reminder_at && (
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: '#cba6ff',
                            marginBottom: '0.75rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                          }}
                        >
                          <span
                            style={{
                              padding: '0.15rem 0.5rem',
                              borderRadius: '999px',
                              border: '1px solid var(--color-secondary)',
                              backgroundColor: 'rgba(63, 160, 201, 0.12)',
                              fontSize: '0.7rem',
                            }}
                          >
                            🔔 {new Date(item.reminder_at).toLocaleDateString()}
                          </span>
                        </p>
                      )}
                    </>
                  )}
                  
                  {/* Status badge - display only for single items, batches show on click */}
                  {viewMode === 'list' && !isBatch && (
                    <>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            cycleStatus(item.id, item.status);
                          }}
                          style={{
                            padding: '0.35rem 0.75rem',
                            fontSize: '0.75rem',
                            borderRadius: '999px',
                            border: '2px solid',
                            borderColor: item.status === 'sealed' ? 'var(--color-success)' : item.status === 'opened' ? '#facc15' : '#6b7280',
                            backgroundColor: item.status === 'sealed' ? 'rgba(107, 175, 74, 0.15)' : item.status === 'opened' ? 'rgba(250, 204, 21, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                            color: item.status === 'sealed' ? 'var(--color-success)' : item.status === 'opened' ? '#facc15' : '#999',
                            cursor: 'pointer',
                            fontWeight: '600',
                          }}
                        >
                          {item.status === 'sealed' && '🟢 Sealed'}
                          {item.status === 'opened' && '🟡 Opened'}
                          {item.status === 'finished' && '⚪ Finished'}
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <span style={{ fontSize: '0.9rem' }}>
                            {item.storage_type === 'tin'
                              ? '🥫'
                              : item.storage_type === 'jar'
                              ? '🫙'
                              : item.storage_type === 'pouch'
                              ? '👝'
                              : item.storage_type
                              ? '📦'
                              : ''}
                          </span>
                          <select
                            value={item.storage_type || ''}
                            onChange={(e) => updateStorageType(item.id, e.target.value as any)}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.4rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '6px', color: 'var(--color-text)' }}
                          >
                            <option value="">None</option>
                            <option value="tin">Tin</option>
                            <option value="jar">Jar</option>
                            <option value="pouch">Pouch</option>
                            <option value="bulk">Bulk</option>
                          </select>
                        </div>
                      </div>

                      {/* Action Buttons - Single items only */}
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/cellar/edit/tobacco/${item.id}`);
                          }}
                          style={{
                            padding: '0.4rem 0.75rem',
                            fontSize: '0.75rem',
                            backgroundColor: 'var(--color-card)',
                            border: '1px solid var(--color-accent)', 
                            color: 'var(--color-accent)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                          }}
                        >
                          <Edit size={12} />
                          Edit
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteTobacco(item.id, item.blend_name);
                          }}
                          style={{
                            padding: '0.4rem 0.75rem',
                            fontSize: '0.75rem',
                            backgroundColor: 'var(--color-card)',
                            border: '1px solid var(--color-danger)',
                            color: 'var(--color-danger)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                          }}
                        >
                          <Trash2 size={12} />
                          Delete
                        </button>
                      </div>
                    </>
                  )}

                  {/* Batch items show hint to click */}
                  {viewMode === 'list' && isBatch && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--color-accent)', fontStyle: 'italic' }}>
                      Click to view and manage all items
                    </p>
                  )}
                </div>
              </div>
              );
            })}

            {activeTab === 'pipes' && pipes.map((item) => (
              <div 
                key={item.id} 
                className="card" 
                style={{ 
                  display: viewMode === 'grid' ? 'block' : 'flex', 
                  gap: viewMode === 'grid' ? undefined : '1rem',
                  cursor: viewMode === 'grid' ? 'pointer' : 'default',
                  transition: 'border-color 0.2s',
                }}
                onClick={() => {
                  if (viewMode === 'grid') {
                    setSelectedPipe(item);
                  }
                }}
                onMouseEnter={(e) => {
                  if (viewMode === 'grid') {
                    e.currentTarget.style.borderColor = 'var(--color-accent)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (viewMode === 'grid') {
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                  }
                }}
              >
                {/* Image */}
                {viewMode === 'grid' ? (
                  <div style={{ marginBottom: '0.5rem' }}>
                    {item.image_url ? (
                      <img src={item.image_url} alt={`${item.maker} ${item.shape}`} loading="lazy" style={{ width: '100%', aspectRatio: '2 / 1', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--color-border)' }} />
                    ) : (
                      <div style={{ width: '100%', aspectRatio: '2 / 1', backgroundColor: '#1f1f1f', borderRadius: '8px', border: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>No Image</div>
                    )}
                  </div>
                ) : item.image_url ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPipePhotoViewerSrc(item.image_url || null);
                      setPipePhotoViewerAlt(`${item.maker} ${item.shape}`);
                    }}
                    aria-label={`View ${item.maker} ${item.shape} photo`}
                    style={{
                      padding: 0,
                      border: 'none',
                      background: 'none',
                      cursor: 'zoom-in',
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={item.image_url}
                      alt={`${item.maker} ${item.shape}`}
                      loading="lazy"
                      style={{
                        width: '120px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        display: 'block',
                      }}
                    />
                  </button>
                ) : (
                  <div style={{
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#1f1f1f',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <DoorOpen size={32} color="#666" />
                  </div>
                )}

                {/* Info */}
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: viewMode === 'grid' ? '0.95rem' : '1rem', fontWeight: '600', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.maker} {item.shape}
                  </h3>
                  {item.style && (
                    <p style={{ fontSize: '0.875rem', color: '#d3de5e', marginBottom: '0.5rem' }}>
                      {item.style}
                    </p>
                  )}
                  {viewMode === 'list' && (
                    <p style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
                      Purchased: {new Date(item.purchase_date).toLocaleDateString()}
                    </p>
                  )}
                  {item.last_cleaned_date && (
          <p style={{ fontSize: '0.75rem', color: 'var(--color-success)', marginBottom: '0.75rem' }}>
                      Cleaned: {new Date(item.last_cleaned_date).toLocaleDateString()}
                    </p>
                  )}
                  {!item.last_cleaned_date && (
                    <div style={{ marginBottom: '0.75rem' }}></div>
                  )}

                  {/* Action Buttons */}
                  {viewMode === 'list' && (
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCleaningPipeId(item.id);
                        setCleanedDate(formatDateForInput());
                      }}
                      style={{
                        padding: '0.4rem 0.75rem',
                        fontSize: '0.75rem',
                        backgroundColor: 'var(--color-card)',
                        border: '1px solid var(--color-success)',
                        color: 'var(--color-success)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                      }}
                    >
                      <Droplet size={12} />
                      Clean
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/cellar/edit/pipe/${item.id}`);
                      }}
                      style={{
                        padding: '0.4rem 0.75rem',
                        fontSize: '0.75rem',
                        backgroundColor: 'var(--color-card)',
                        border: '1px solid var(--color-accent)',
                        color: 'var(--color-accent)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                      }}
                    >
                      <Edit size={12} />
                      Edit
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deletePipe(item.id, `${item.maker} ${item.shape}`);
                      }}
                      style={{
                        padding: '0.4rem 0.75rem',
                        fontSize: '0.75rem',
                        backgroundColor: 'var(--color-card)',
                        border: '1px solid var(--color-danger)',
                        color: 'var(--color-danger)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                      }}
                    >
                      <Trash2 size={12} />
                      Delete
                    </button>
                  </div>
                  )}
                </div>
              </div>
            ))}

            {activeTab === 'lighters' && lighters.map((item) => (
              <div 
                key={item.id} 
                className="card" 
                style={{ 
                  display: viewMode === 'grid' ? 'block' : 'flex', 
                  gap: viewMode === 'grid' ? undefined : '1rem',
                  cursor: viewMode === 'grid' ? 'pointer' : 'default',
                  transition: 'border-color 0.2s',
                }}
                onClick={() => {
                  if (viewMode === 'grid') {
                    setSelectedLighter(item);
                  }
                }}
                onMouseEnter={(e) => {
                  if (viewMode === 'grid') {
                    e.currentTarget.style.borderColor = 'var(--color-accent)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (viewMode === 'grid') {
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                  }
                }}
              >
                {/* Image */}
                {viewMode === 'grid' ? (
                  <div style={{ marginBottom: '0.5rem' }}>
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.brand} loading="lazy" style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--color-border)' }} />
                    ) : (
                      <div style={{ width: '100%', aspectRatio: '1 / 1', backgroundColor: '#1f1f1f', borderRadius: '8px', border: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>No Image</div>
                    )}
                  </div>
                ) : item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.brand}
                    loading="lazy"
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div style={{
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#1f1f1f',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <DoorOpen size={32} color="#666" />
                  </div>
                )}

                {/* Info */}
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: viewMode === 'grid' ? '0.95rem' : '1rem', fontWeight: '600', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.brand}
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#d3de5e', marginBottom: '0.5rem' }}>
                    {item.type === 'lighter' ? 'Lighter' : 'Matches'}
                    {item.style && ` • ${item.style}`}
                  </p>
                  {viewMode === 'list' && (
                    <p style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.75rem' }}>
                      {item.colour && `${item.colour} • `}
                      Purchased: {new Date(item.purchase_date).toLocaleDateString()}
                    </p>
                  )}

                  {/* Action Buttons */}
                  {viewMode === 'list' && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/cellar/edit/lighter/${item.id}`);
                      }}
                      style={{
                        padding: '0.4rem 0.75rem',
                        fontSize: '0.75rem',
                        backgroundColor: 'var(--color-card)',
                        border: '1px solid var(--color-accent)',
                        color: 'var(--color-accent)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                      }}
                    >
                      <Edit size={12} />
                      Edit
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteLighter(item.id, item.brand);
                      }}
                      style={{
                        padding: '0.4rem 0.75rem',
                        fontSize: '0.75rem',
                        backgroundColor: 'var(--color-card)',
                        border: '1px solid var(--color-danger)',
                        color: 'var(--color-danger)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                      }}
                    >
                      <Trash2 size={12} />
                      Delete
                    </button>
                  </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Removed old floating action button in favor of header CTA */}

      <BottomNav />
      {/* Wish Add Modal */}
      {showWishModal && (
        <div style={{ position: 'fixed', inset: 0 as any, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '12px', width: '100%', maxWidth: '420px', padding: '1rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--color-accent)' }}>Add to Wish List</h3>
            <WishForm onAdded={(w) => { setWishList([w, ...wishList]); setShowWishModal(false); }} />
            <div style={{ marginTop: '0.5rem', textAlign: 'right' }}>
              <button onClick={() => setShowWishModal(false)} style={{ padding: '0.5rem 0.75rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: '#ccc', borderRadius: '6px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Wish Modal */}
      {editingWish && (
        <div style={{ position: 'fixed', inset: 0 as any, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '12px', width: '100%', maxWidth: '420px', padding: '1rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--color-accent)' }}>Edit Wish</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <input placeholder="Blender" value={editingWish.brand} onChange={(e) => setEditingWish({ ...editingWish, brand: e.target.value })} style={{ width: '100%' }} />
              <input placeholder="Blend" value={editingWish.blend_name} onChange={(e) => setEditingWish({ ...editingWish, blend_name: e.target.value })} style={{ width: '100%' }} />
            </div>
            <textarea placeholder="Notes (optional)" value={editingWish.notes || ''} onChange={(e) => setEditingWish({ ...editingWish, notes: e.target.value })} rows={2} style={{ width: '100%', marginTop: '0.5rem' }} />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button onClick={() => setEditingWish(null)} style={{ flex: 1, padding: '0.6rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: '#ccc', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={saveWishEdits} style={{ flex: 1, padding: '0.6rem', backgroundColor: 'var(--color-accent)', border: 'none', color: '#0b0b0b', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }}>Save</button>
            </div>
          </div>
        </div>
      )}
      {/* Wishlist Grid – Expanded Item Modal */}
      {expandedWishId && (
        <div style={{ position: 'fixed', inset: 0 as any, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '12px', width: '100%', maxWidth: '520px', padding: '1rem', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-accent)' }}>Wish Details</h3>
              <button onClick={() => setExpandedWishId(null)} style={{ padding: '0.35rem 0.75rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: '#ccc', borderRadius: '6px', cursor: 'pointer' }}>Close</button>
            </div>
            {wishList.filter((w) => w.id === expandedWishId).map((w) => (
              <WishListCard 
                key={w.id}
                item={w}
                retailers={retailers}
                onEdit={setEditingWish}
                onDelete={async (id) => { await deleteWish(id); setExpandedWishId(null); }}
                onPurchase={(item) => { handlePurchaseWish(item); setExpandedWishId(null); }}
              />
            ))}
          </div>
        </div>
      )}
      {/* Reminder Modal */}
      {reminderTargetId && (
        <div style={{ position: 'fixed', inset: 0 as any, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '12px', width: '100%', maxWidth: '420px', padding: '1rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--color-accent)' }}>Set Reminder</h3>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <input
                type="number"
                min={1}
                max={reminderUnit === 'weeks' ? 6 : 24}
                value={reminderQty}
                onChange={(e) => {
                  const raw = parseInt(e.target.value || '1', 10);
                  const max = reminderUnit === 'weeks' ? 6 : 24;
                  setReminderQty(Math.max(1, Math.min(raw, max)));
                }}
                style={{ flex: 1, padding: '0.5rem' }}
              />
              <select
                value={reminderUnit}
                onChange={(e) => {
                  const nextUnit = e.target.value as 'weeks' | 'months';
                  setReminderUnit(nextUnit);
                  const max = nextUnit === 'weeks' ? 6 : 24;
                  setReminderQty((prev) => {
                    const safePrev = prev || 1;
                    return Math.max(1, Math.min(safePrev, max));
                  });
                }}
                style={{ flex: 1, padding: '0.5rem' }}
              >
                <option value="weeks">Weeks</option>
                <option value="months">Months</option>
              </select>
            </div>
            <textarea value={reminderNotes} onChange={(e) => setReminderNotes(e.target.value)} placeholder="Notes (optional)" rows={3} style={{ width: '100%', marginBottom: '0.75rem' }} />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => setReminderTargetId(null)} style={{ flex: 1, padding: '0.6rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: '#ccc', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={saveReminder} style={{ flex: 1, padding: '0.6rem', backgroundColor: 'var(--color-accent)', border: 'none', color: '#0b0b0b', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Detail Modal */}
      {selectedBatchGroup && (
        <div style={{ position: 'fixed', inset: 0 as any, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem', overflow: 'auto' }}>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid var(--color-accent)', borderRadius: '12px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto', padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--color-accent)' }}>
                  {selectedBatchGroup[0].brand} - {selectedBatchGroup[0].blend_name}
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#999', marginTop: '0.25rem' }}>
                  {selectedBatchGroup.length} items in this batch
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button 
                  onClick={() => {
                    const item = selectedBatchGroup[0];
                    const params = new URLSearchParams({
                      type: 'tobacco',
                      brand: item.brand || '',
                      blend: item.blend_name || '',
                      amount: item.amount.toString(),
                      unit: item.unit,
                      storage: item.storage_type || ''
                    });
                    // Only add batch_id if it exists (don't add empty string)
                    if (item.batch_id) {
                      params.set('batch_id', item.batch_id);
                    }
                    navigate(`/cellar/add?${params.toString()}`);
                    setSelectedBatchGroup(null);
                  }}
                  style={{ 
                    padding: '0.5rem 1rem', 
                    backgroundColor: 'var(--color-card)', 
                    border: '1px solid var(--color-accent)', 
                    color: 'var(--color-accent)', 
                    borderRadius: '6px', 
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem'
                  }}
                >
                  <Plus size={14} /> Add More
                </button>
                <button 
                  onClick={() => setSelectedBatchGroup(null)} 
                  style={{ 
                    padding: '0.5rem 1rem', 
                    backgroundColor: 'var(--color-card)', 
                    border: '1px solid var(--color-border)', 
                    color: '#ccc', 
                    borderRadius: '6px', 
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {selectedBatchGroup.map((item, index) => (
                <div 
                  key={item.id} 
                  style={{ 
                    backgroundColor: '#0d0d0d', 
                    border: '1px solid var(--color-border)', 
                    borderRadius: '8px', 
                    padding: '1rem' 
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-accent)' }}>
                      Item #{index + 1}
                    </h4>
                    <span style={{ fontSize: '0.75rem', color: '#666' }}>
                      {item.amount}{item.unit === 'grams' ? 'g' : 'oz'}
                    </span>
                  </div>

                  <p style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.25rem' }}>
                    Purchased: {new Date(item.purchase_date).toLocaleDateString()}
                  </p>
                  {item.tinned_date && (
                    <>
                      <p style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.25rem' }}>
                        Tinned: {new Date(item.tinned_date).toLocaleDateString()}
                      </p>
                      {(() => {
                        const ageDays = calculateAgeInDays(item.tinned_date, item.purchase_date);
                        return ageDays !== null ? (
                          <p style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.25rem' }}>
                            Age: {ageDays} day{ageDays !== 1 ? 's' : ''}
                          </p>
                        ) : null;
                      })()}
                    </>
                  )}
                  {item.status === 'opened' && item.opened_date && (
                    <p style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.75rem' }}>
                      Opened: {new Date(item.opened_date).toLocaleDateString()}
                    </p>
                  )}

                  {(sessionStats[item.id]?.count || sessionStats[item.id]?.average != null) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                      {sessionStats[item.id]?.average != null && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#ffb347' }}>
                          <Star size={14} fill="#ffb347" color="#ffb347" />
                          {sessionStats[item.id]!.average!.toFixed(1)} / 10
                        </span>
                      )}
                      {sessionStats[item.id]?.count ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', color: 'var(--color-accent)' }}>
                          <NotebookPen size={14} />
                          {sessionStats[item.id]!.count}
                          <span style={{ color: '#888', fontSize: '0.75rem' }}>session{sessionStats[item.id]!.count > 1 ? 's' : ''}</span>
                        </span>
                      ) : null}
                    </div>
                  )}

                  {/* Status & Storage */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <button
                      onClick={() => cycleStatus(item.id, item.status)}
                      style={{
                        padding: '0.35rem 0.75rem',
                        fontSize: '0.75rem',
                        borderRadius: '999px',
                        border: '2px solid',
                        borderColor: item.status === 'sealed' ? 'var(--color-success)' : item.status === 'opened' ? '#facc15' : '#6b7280',
                        backgroundColor: item.status === 'sealed' ? 'rgba(107, 175, 74, 0.15)' : item.status === 'opened' ? 'rgba(250, 204, 21, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                        color: item.status === 'sealed' ? 'var(--color-success)' : item.status === 'opened' ? '#facc15' : '#999',
                        cursor: 'pointer',
                        fontWeight: '600',
                      }}
                    >
                      {item.status === 'sealed' && '🟢 Sealed'}
                      {item.status === 'opened' && '🟡 Opened'}
                      {item.status === 'finished' && '⚪ Finished'}
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <span style={{ fontSize: '0.9rem' }}>
                        {item.storage_type === 'tin'
                          ? '🥫'
                          : item.storage_type === 'jar'
                          ? '🫙'
                          : item.storage_type === 'pouch'
                          ? '👝'
                          : item.storage_type
                          ? '📦'
                          : ''}
                      </span>
                      <select
                        value={item.storage_type || ''}
                        onChange={(e) => updateStorageType(item.id, e.target.value as any)}
                        style={{ fontSize: '0.75rem', padding: '0.35rem 0.5rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '6px', color: 'var(--color-text)' }}
                      >
                        <option value="">None</option>
                        <option value="tin">Tin</option>
                        <option value="jar">Jar</option>
                        <option value="pouch">Pouch</option>
                        <option value="bulk">Bulk</option>
                      </select>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <button
                      onClick={() => navigate(`/sessions/new?cellar_tobacco_id=${item.id}`)}
                      style={{
                        padding: '0.4rem 0.75rem',
                        fontSize: '0.75rem',
                        backgroundColor: 'var(--color-card)',
                        border: '1px solid var(--color-accent)',
                        color: 'var(--color-accent)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem'
                      }}
                    >
                      <NotebookPen size={12} /> Journal
                    </button>
                    <button 
                      onClick={() => { 
                        setSelectedSingleItem(null);
                        setReminderTargetId(item.id); 
                        setReminderQty(4); 
                        setReminderUnit('weeks'); 
                        setReminderNotes(item.reminder_notes || ''); 
                      }} 
                      style={{ 
                        padding: '0.4rem 0.75rem', 
                        fontSize: '0.75rem', 
                        backgroundColor: 'var(--color-card)', 
                        border: '1px solid var(--color-secondary)', 
                        color: 'var(--color-secondary)', 
                        borderRadius: '6px', 
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Set Reminder
                    </button>
                    
                    <button 
                      onClick={() => navigate(`/cellar/edit/tobacco/${item.id}`)} 
                      style={{ 
                        padding: '0.4rem 0.75rem', 
                        fontSize: '0.75rem', 
                        backgroundColor: 'var(--color-card)', 
                        border: '1px solid var(--color-accent)', 
                        color: 'var(--color-accent)', 
                        borderRadius: '6px', 
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.35rem' 
                      }}
                    >
                      <Edit size={12} /> Edit
                    </button>
                    
                    <button 
                      onClick={() => {
                        deleteTobacco(item.id, item.blend_name);
                        // If this was the last item in the batch, close the modal
                        if (selectedBatchGroup.length === 1) {
                          setSelectedBatchGroup(null);
                        }
                      }} 
                      style={{ 
                        padding: '0.4rem 0.75rem', 
                        fontSize: '0.75rem', 
                        backgroundColor: 'var(--color-card)', 
                        border: '1px solid var(--color-danger)', 
                        color: 'var(--color-danger)', 
                        borderRadius: '6px', 
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.35rem' 
                      }}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>

                  {item.reminder_at && (
                    <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.6rem 0.5rem 0.6rem', backgroundColor: 'rgba(63, 160, 201, 0.12)', border: '1px solid var(--color-secondary)', borderRadius: '6px', position: 'relative' }}>
                      <button
                        onClick={() => clearReminder(item.id)}
                        aria-label="Clear reminder"
                        style={{
                          position: 'absolute',
                          top: '6px',
                          right: '6px',
                          background: 'transparent',
                          border: 'none',
                          color: '#bf8bff',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          lineHeight: 1,
                          padding: '0.1rem 0.25rem'
                        }}
                      >
                        ×
                      </button>
                      <p style={{ fontSize: '0.75rem', color: '#cba6ff', marginRight: '1.25rem' }}>
                        🔔 Reminder: {new Date(item.reminder_at).toLocaleDateString()}
                        {item.reminder_notes && <span style={{ display: 'block', marginTop: '0.25rem', color: '#bbb' }}>{item.reminder_notes}</span>}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Single Item Quick Actions Modal */}
      {selectedSingleItem && (
        <div style={{ position: 'fixed', inset: 0 as any, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '12px', width: '100%', maxWidth: '520px', padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-accent)' }}>
                  {selectedSingleItem.brand} - {selectedSingleItem.blend_name}
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#999', marginTop: '0.25rem' }}>
                  {selectedSingleItem.amount}{selectedSingleItem.unit === 'grams' ? 'g' : 'oz'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button 
                  onClick={() => {
                    const item = selectedSingleItem;
                    const params = new URLSearchParams({
                      type: 'tobacco',
                      brand: item.brand || '',
                      blend: item.blend_name || '',
                      amount: item.amount.toString(),
                      unit: item.unit,
                      storage: item.storage_type || ''
                    });
                    // Pass the existing item ID so we can update it to join a batch
                    if (item.id) {
                      params.set('existing_item_id', item.id);
                    }
                    navigate(`/cellar/add?${params.toString()}`);
                    setSelectedSingleItem(null);
                  }}
                  style={{ 
                    padding: '0.4rem 0.75rem', 
                    backgroundColor: 'var(--color-card)', 
                    border: '1px solid var(--color-accent)', 
                    color: 'var(--color-accent)', 
                    borderRadius: '6px', 
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem'
                  }}
                >
                  <Plus size={12} /> Add More
                </button>
                <button onClick={() => setSelectedSingleItem(null)} style={{ padding: '0.4rem 0.75rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: '#ccc', borderRadius: '6px', cursor: 'pointer' }}>Close</button>
              </div>
            </div>

            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {/* Key dates & age - mirror list view details */}
              <div>
                <p style={{ fontSize: '0.8rem', color: '#999', marginBottom: '0.25rem' }}>
                  Purchased: {new Date(selectedSingleItem.purchase_date).toLocaleDateString()}
                </p>
                {selectedSingleItem.tinned_date && (
                  <>
                    <p style={{ fontSize: '0.8rem', color: '#999', marginBottom: '0.25rem' }}>
                      Tinned: {new Date(selectedSingleItem.tinned_date).toLocaleDateString()}
                    </p>
                    {(() => {
                      const ageDays = calculateAgeInDays(selectedSingleItem.tinned_date!, selectedSingleItem.purchase_date);
                      return ageDays !== null ? (
                        <p style={{ fontSize: '0.8rem', color: '#999', marginBottom: '0.25rem' }}>
                          Age: {ageDays} day{ageDays !== 1 ? 's' : ''}
                        </p>
                      ) : null;
                    })()}
                  </>
                )}
                {selectedSingleItem.status === 'opened' && selectedSingleItem.opened_date && (
                  <p style={{ fontSize: '0.8rem', color: '#999', marginBottom: '0.25rem' }}>
                    Opened: {new Date(selectedSingleItem.opened_date).toLocaleDateString()}
                  </p>
                )}
              </div>

              {/* Status & Storage */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <button
                  onClick={() => cycleStatus(selectedSingleItem.id, selectedSingleItem.status)}
                  style={{
                    padding: '0.35rem 0.75rem',
                    fontSize: '0.75rem',
                    borderRadius: '999px',
                    border: '2px solid',
                    borderColor: selectedSingleItem.status === 'sealed' ? 'var(--color-success)' : selectedSingleItem.status === 'opened' ? '#facc15' : '#6b7280',
                    backgroundColor: selectedSingleItem.status === 'sealed' ? 'rgba(107, 175, 74, 0.15)' : selectedSingleItem.status === 'opened' ? 'rgba(250, 204, 21, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                    color: selectedSingleItem.status === 'sealed' ? 'var(--color-success)' : selectedSingleItem.status === 'opened' ? '#facc15' : '#999',
                    cursor: 'pointer',
                    fontWeight: '600',
                  }}
                >
                  {selectedSingleItem.status === 'sealed' && '🟢 Sealed'}
                  {selectedSingleItem.status === 'opened' && '🟡 Opened'}
                  {selectedSingleItem.status === 'finished' && '⚪ Finished'}
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <span style={{ fontSize: '0.9rem' }}>
                    {selectedSingleItem.storage_type === 'tin'
                      ? '🥫'
                      : selectedSingleItem.storage_type === 'jar'
                      ? '🫙'
                      : selectedSingleItem.storage_type === 'pouch'
                      ? '👝'
                      : selectedSingleItem.storage_type
                      ? '📦'
                      : ''}
                  </span>
                  <select
                    value={selectedSingleItem.storage_type || ''}
                    onChange={(e) => updateStorageType(selectedSingleItem.id, e.target.value as any)}
                    style={{ fontSize: '0.75rem', padding: '0.35rem 0.5rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '6px', color: 'var(--color-text)' }}
                  >
                    <option value="">None</option>
                    <option value="tin">Tin</option>
                    <option value="jar">Jar</option>
                    <option value="pouch">Pouch</option>
                    <option value="bulk">Bulk</option>
                  </select>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <button 
                  onClick={() => { 
                    setSelectedSingleItem(null);
                    setReminderTargetId(selectedSingleItem.id); 
                    setReminderQty(4); 
                    setReminderUnit('weeks'); 
                    setReminderNotes(selectedSingleItem.reminder_notes || ''); 
                  }} 
                  style={{ 
                    padding: '0.4rem 0.75rem', 
                    fontSize: '0.75rem', 
                    backgroundColor: 'var(--color-card)', 
                    border: '1px solid var(--color-secondary)', 
                    color: 'var(--color-secondary)', 
                    borderRadius: '6px', 
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Set Reminder
                </button>

                <button 
                  onClick={() => navigate(`/cellar/edit/tobacco/${selectedSingleItem.id}`)} 
                  style={{ 
                    padding: '0.4rem 0.75rem', 
                    fontSize: '0.75rem', 
                    backgroundColor: 'var(--color-card)', 
                    border: '1px solid var(--color-accent)', 
                    color: 'var(--color-accent)', 
                    borderRadius: '6px', 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.35rem' 
                  }}
                >
                  <Edit size={12} /> Edit
                </button>

                <button 
                  onClick={() => { deleteTobacco(selectedSingleItem.id, selectedSingleItem.blend_name); setSelectedSingleItem(null); }} 
                  style={{ 
                    padding: '0.4rem 0.75rem', 
                    fontSize: '0.75rem', 
                    backgroundColor: 'var(--color-card)', 
                    border: '1px solid var(--color-danger)', 
                    color: 'var(--color-danger)', 
                    borderRadius: '6px', 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.35rem' 
                  }}
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>

              {(sessionStats[selectedSingleItem.id]?.average != null || sessionStats[selectedSingleItem.id]?.count) && (
                <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.8rem' }}>
                  {sessionStats[selectedSingleItem.id]?.average != null && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#ffb347' }}>
                      <Star size={14} fill="#ffb347" color="#ffb347" />
                      {sessionStats[selectedSingleItem.id]!.average!.toFixed(1)} / 10
                    </span>
                  )}
                  {sessionStats[selectedSingleItem.id]?.count ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: 'var(--color-accent)' }}>
                      <NotebookPen size={14} />
                      {sessionStats[selectedSingleItem.id]!.count}
                      <span style={{ color: '#888', fontSize: '0.75rem' }}>session{sessionStats[selectedSingleItem.id]!.count > 1 ? 's' : ''}</span>
                    </span>
                  ) : null}
                </div>
              )}

              {selectedSingleItem.reminder_at && (
                <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.6rem 0.5rem 0.6rem', backgroundColor: 'rgba(63, 160, 201, 0.12)', border: '1px solid var(--color-secondary)', borderRadius: '6px', position: 'relative' }}>
                  <button
                    onClick={() => clearReminder(selectedSingleItem.id)}
                    aria-label="Clear reminder"
                    style={{
                      position: 'absolute',
                      top: '6px',
                      right: '6px',
                      background: 'transparent',
                      border: 'none',
                      color: '#bf8bff',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      lineHeight: 1,
                      padding: '0.1rem 0.25rem'
                    }}
                  >
                    ×
                  </button>
                  <p style={{ fontSize: '0.75rem', color: '#cba6ff', marginRight: '1.25rem' }}>
                    🔔 Reminder: {new Date(selectedSingleItem.reminder_at).toLocaleDateString()}
                    {selectedSingleItem.reminder_notes && <span style={{ display: 'block', marginTop: '0.25rem', color: '#bbb' }}>{selectedSingleItem.reminder_notes}</span>}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Clean Pipe Modal */}
      {cleaningPipeId && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid var(--color-success)',
            padding: '2rem',
            maxWidth: '400px',
            width: '100%'
          }}>
            <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: 'var(--color-success)' }}>
              Mark Pipe as Cleaned
            </h3>
            
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#cfcfcf' }}>
              Cleaned Date:
            </label>
            <input
              type="date"
              value={cleanedDate}
              onChange={(e) => setCleanedDate(e.target.value)}
              max={formatDateForInput()}
              style={{
                width: '100%',
                marginBottom: '1.5rem',
                padding: '0.75rem',
                fontSize: '1rem',
                backgroundColor: 'var(--color-card)',
                color: 'var(--color-text)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px'
              }}
            />

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setCleaningPipeId(null)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: 'transparent',
                  border: '1px solid #666',
                  color: '#999',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCleanPipe}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-success)',
                  border: 'none',
                  color: '#0b0b0b',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pipe Photo Viewer */}
      {pipePhotoViewerSrc && (
        <div
          onClick={() => setPipePhotoViewerSrc(null)}
          style={{
            position: 'fixed',
            inset: 0 as any,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '1rem',
          }}
        >
          <img
            src={pipePhotoViewerSrc}
            alt={pipePhotoViewerAlt || 'Pipe photo'}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '92vw',
              maxHeight: '85vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: '12px',
              backgroundColor: '#111',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.45)',
            }}
          />
        </div>
      )}

      {/* Pipe Quick Actions Modal */}
      {selectedPipe && (
        <div style={{ position: 'fixed', inset: 0 as any, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '12px', width: '100%', maxWidth: '520px', padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-accent)' }}>
                  {selectedPipe.maker} {selectedPipe.shape}
                </h3>
                {selectedPipe.style && (
                  <p style={{ fontSize: '0.875rem', color: '#999', marginTop: '0.25rem' }}>
                    {selectedPipe.style}
                  </p>
                )}
                <p style={{ fontSize: '0.875rem', color: '#999', marginTop: '0.25rem' }}>
                  Purchased: {new Date(selectedPipe.purchase_date).toLocaleDateString()}
                </p>
                {selectedPipe.last_cleaned_date && (
                  <p style={{ fontSize: '0.875rem', color: 'var(--color-success)', marginTop: '0.25rem' }}>
                    Cleaned: {new Date(selectedPipe.last_cleaned_date).toLocaleDateString()}
                  </p>
                )}
              </div>
              <button onClick={() => setSelectedPipe(null)} style={{ padding: '0.4rem 0.75rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: '#ccc', borderRadius: '6px', cursor: 'pointer' }}>Close</button>
            </div>

            {selectedPipe.image_url && (
              <button
                type="button"
                onClick={() => {
                  setPipePhotoViewerAlt(`${selectedPipe.maker} ${selectedPipe.shape}`);
                  setPipePhotoViewerSrc(selectedPipe.image_url ?? null);
                }}
                aria-label={`View ${selectedPipe.maker} ${selectedPipe.shape} photo`}
                style={{
                  width: '100%',
                  marginTop: '0.75rem',
                  padding: 0,
                  background: 'none',
                  border: 'none',
                  cursor: 'zoom-in',
                }}
              >
                <img
                  src={selectedPipe.image_url}
                  alt={`${selectedPipe.maker} ${selectedPipe.shape}`}
                  loading="lazy"
                  style={{
                    width: '100%',
                    aspectRatio: '2 / 1',
                    objectFit: 'cover',
                    borderRadius: '10px',
                    border: '1px solid var(--color-border)',
                    display: 'block',
                  }}
                />
              </button>
            )}

            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {/* Actions */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <button
                  onClick={() => {
                    setCleaningPipeId(selectedPipe.id);
                    setCleanedDate(formatDateForInput());
                    setSelectedPipe(null);
                  }}
                  style={{
                    padding: '0.4rem 0.75rem',
                    fontSize: '0.75rem',
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-success)',
                    color: 'var(--color-success)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                  }}
                >
                  <Droplet size={12} />
                  Mark Cleaned
                </button>

                <button 
                  onClick={() => {
                    navigate(`/cellar/edit/pipe/${selectedPipe.id}`);
                    setSelectedPipe(null);
                  }} 
                  style={{ 
                    padding: '0.4rem 0.75rem', 
                    fontSize: '0.75rem', 
                    backgroundColor: 'var(--color-card)', 
                    border: '1px solid var(--color-accent)', 
                    color: 'var(--color-accent)', 
                    borderRadius: '6px', 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.35rem' 
                  }}
                >
                  <Edit size={12} /> Edit
                </button>

                <button 
                  onClick={() => { 
                    deletePipe(selectedPipe.id, `${selectedPipe.maker} ${selectedPipe.shape}`); 
                    setSelectedPipe(null); 
                  }} 
                  style={{ 
                    padding: '0.4rem 0.75rem', 
                    fontSize: '0.75rem', 
                    backgroundColor: 'var(--color-card)', 
                    border: '1px solid var(--color-danger)', 
                    color: 'var(--color-danger)', 
                    borderRadius: '6px', 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.35rem' 
                  }}
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lighter Quick Actions Modal */}
      {selectedLighter && (
        <div style={{ position: 'fixed', inset: 0 as any, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '12px', width: '100%', maxWidth: '520px', padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-accent)' }}>
                  {selectedLighter.brand}
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#999', marginTop: '0.25rem' }}>
                  {selectedLighter.type === 'lighter' ? 'Lighter' : 'Matches'}
                  {selectedLighter.style && ` • ${selectedLighter.style}`}
                </p>
                {selectedLighter.colour && (
                  <p style={{ fontSize: '0.875rem', color: '#999', marginTop: '0.25rem' }}>
                    {selectedLighter.colour}
                  </p>
                )}
                <p style={{ fontSize: '0.875rem', color: '#999', marginTop: '0.25rem' }}>
                  Purchased: {new Date(selectedLighter.purchase_date).toLocaleDateString()}
                </p>
              </div>
              <button onClick={() => setSelectedLighter(null)} style={{ padding: '0.4rem 0.75rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: '#ccc', borderRadius: '6px', cursor: 'pointer' }}>Close</button>
            </div>

            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {/* Actions */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <button 
                  onClick={() => {
                    navigate(`/cellar/edit/lighter/${selectedLighter.id}`);
                    setSelectedLighter(null);
                  }} 
                  style={{ 
                    padding: '0.4rem 0.75rem', 
                    fontSize: '0.75rem', 
                    backgroundColor: 'var(--color-card)', 
                    border: '1px solid var(--color-accent)', 
                    color: 'var(--color-accent)', 
                    borderRadius: '6px', 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.35rem' 
                  }}
                >
                  <Edit size={12} /> Edit
                </button>

                <button 
                  onClick={() => { 
                    deleteLighter(selectedLighter.id, selectedLighter.brand); 
                    setSelectedLighter(null); 
                  }} 
                  style={{ 
                    padding: '0.4rem 0.75rem', 
                    fontSize: '0.75rem', 
                    backgroundColor: 'var(--color-card)', 
                    border: '1px solid var(--color-danger)', 
                    color: 'var(--color-danger)', 
                    borderRadius: '6px', 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.35rem' 
                  }}
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
